// The phone's foreground sender for a lot's queued photos and videos (BL-2815, ADR 070 — Ingest Protocol
// § 4, ADR 074 — Upload Standard): each queued add becomes one repeat-safe `PUT /api/lots/:id/media/:mediaId`,
// one at a time, before the lot's state request. The thumbnail is made now, from the staged file.
import { markFailed, markRetryLater, type LotDesiredState } from '@auctogon/admin-isomorphic/isomorphic/offline/lot-desired-state';
import { classifyIngestResponse } from '@auctogon/admin-isomorphic/isomorphic/offline/media-ingest-outcome';
import { stagedPosterIdOf } from '@auctogon/admin-isomorphic/isomorphic/offline/offline-attachments';
import { filesOfAdd, ingestPlacement, mediaOf, withoutAdd, withoutAdds, type MediaAdd } from '@auctogon/admin-isomorphic/isomorphic/offline/lot-state-media';
import type { ReplayError } from '@auctogon/admin-isomorphic/isomorphic/offline/change-queue-outcome';
import { makeSendThumbnail } from '../../api-client/send-thumbnail';
import type { OfflineMediaStaging, OfflineStore } from '../../gateways/native-device';
import { ReplayTransportError, type ReplayMediaInput, type ReplayTransport } from './replay-transport';

/** What the media sender needs from the replay that owns it. */
export interface LotMediaSenderDeps {
  store: OfflineStore;
  transport: ReplayTransport;
  /** The staged files, or null/absent on a build with none. */
  media?: OfflineMediaStaging | null;
  notify: () => void;
  /** Not aborted, closed or offline, and still the session's scope (BUG-2064). */
  maySend: () => boolean | Promise<boolean>;
  logError?: (message: string, err: Error) => void;
}

/** How one lot's media went: all sent, or the first thing that stopped it. */
export type LotMediaOutcome = { ended: 'ok' | 'failed' | 'retry' | 'gone'; sent: number };

const FILE_GONE: ReplayError = {
  message: 'The file for this photo or video is no longer on this device.',
  code: null,
  httpStatus: null,
  fieldNames: [],
};
const READ_FAILED: ReplayError = {
  message: 'The file for this photo or video could not be read on this device just now. It is tried again.',
  code: null,
  httpStatus: null,
  fieldNames: [],
};
const NO_PREVIEW: ReplayError = {
  message: 'A preview for this photo or video could not be made on this device.',
  code: null,
  httpStatus: null,
  fieldNames: [],
};

type Prepared =
  | { ok: true; input: Omit<ReplayMediaInput, 'signal' | 'lotId'>; files: string[] }
  | { ok: false; error: ReplayError; retry?: boolean };

/** A staged file that exists but could not be read just now (an I/O or bridge error): tried again, not failed. */
class StagedReadError extends Error {}

/** One staged file, or null when it is gone. A read that fails is logged and thrown as {@link StagedReadError}. */
async function readStaged(deps: LotMediaSenderDeps, id: string | undefined) {
  if (id == null || !deps.media) return null;
  return deps.media.read(id).catch((err: unknown) => {
    const cause = err instanceof Error ? err : new Error(String(err));
    deps.logError?.('change queue: a staged file could not be read', cause);
    throw new StagedReadError('staged file unreadable', { cause });
  });
}

/** Read the add's staged files and build its request, uploading a video's file first. */
async function prepare(deps: LotMediaSenderDeps, input: { lotId: string; state: LotDesiredState; add: MediaAdd }): Promise<Prepared> {
  const { add, state } = input;
  const { transport, logError } = deps;
  const placement = { ...ingestPlacement(mediaOf(state), add), ...(add.caption ? { caption: add.caption } : {}) };
  if (add.stagedFileId == null) {
    // A file uploaded before the phone went offline attaches by its path: no files, because the thumbnail was
    // uploaded beside it at pick time (BL-2816). The server says so if it never arrived.
    if (add.storagePath == null) return { ok: false, error: FILE_GONE };
    return { ok: true, files: [], input: { mediaId: add.id, meta: { ...placement, type: add.type, storagePath: add.storagePath } } };
  }
  const files = filesOfAdd(add);
  try {
    if (add.type === 'photo') {
      const staged = await readStaged(deps, add.stagedFileId);
      if (!staged) return { ok: false, error: FILE_GONE };
      // BL-2682: the pick staged its thumbnail beside the photo. A photo staged by an earlier build has none, so one is made.
      const pickThumbnail = await readStaged(deps, stagedPosterIdOf(add.stagedFileId));
      const thumbnail = pickThumbnail?.blob ?? (await makeSendThumbnail(staged.blob));
      const meta = { ...placement, type: 'photo' as const, contentType: staged.file.mimeType };
      return { ok: true, files, input: { mediaId: add.id, photo: { blob: staged.blob, name: staged.file.name }, thumbnail, meta } };
    }
    // A video's poster first: a missing one costs nothing, and the (large) file is read last and held only while it uploads.
    const poster = add.stagedPosterFileId != null ? await readStaged(deps, add.stagedPosterFileId) : null;
    if (!poster) return { ok: false, error: NO_PREVIEW };
    const thumbnail = await makeSendThumbnail(poster.blob);
    const staged = await readStaged(deps, add.stagedFileId);
    if (!staged) return { ok: false, error: FILE_GONE };
    const up = await transport.upload({ op: 'media', lotId: input.lotId, stagedFileId: add.stagedFileId, file: staged.file, blob: staged.blob });
    if (!up.ok) {
      if (up.unauthorized) throw new ReplayTransportError('change queue: unauthorized');
      return { ok: false, error: { message: up.message, code: null, httpStatus: null, fieldNames: [] }, retry: up.retryLater === true };
    }
    return { ok: true, files, input: { mediaId: add.id, thumbnail, meta: { ...placement, type: 'video', storagePath: up.storagePath } } };
  } catch (err) {
    if (err instanceof ReplayTransportError) throw err;
    if (err instanceof StagedReadError) return { ok: false, error: READ_FAILED, retry: true };
    logError?.('change queue: a staged photo or video could not be prepared', err instanceof Error ? err : new Error(String(err)));
    return { ok: false, error: NO_PREVIEW };
  }
}

/** Take a sent (or dropped) add off the lot's state, and delete its staged files. */
async function finishAdd(deps: LotMediaSenderDeps, input: { lotId: string; add: MediaAdd; files: string[]; landed: boolean }): Promise<void> {
  await deps.store.lotStates.update(input.lotId, (cur) => {
    if (!cur) return cur;
    const media = mediaOf(cur);
    // A Save removed this photo while its request was in flight: it landed, so the removal is owed to the server.
    const cancelled = input.landed && !media.adds.some((a) => a.id === input.add.id) && !media.removed.includes(input.add.id);
    const next = withoutAdd(media, input.add.id);
    return { ...cur, media: cancelled ? { ...next, removed: [...next.removed, input.add.id] } : next };
  });
  for (const id of input.files) {
    await deps.media?.delete(id).catch((err: unknown) => {
      deps.logError?.('change queue: a staged file could not be deleted', err instanceof Error ? err : new Error(String(err)));
      return false;
    });
  }
  deps.notify();
}

const LOT_GONE_MESSAGE = 'This lot is no longer on the server, so its photos and videos were not added.';

/** The server no longer holds the lot: forget every queued add, delete their staged files, and fail the lot once (Discard clears the rest). */
async function dropGoneLot(deps: LotMediaSenderDeps, input: { lotId: string; error: ReplayError }): Promise<void> {
  let files: string[] = [];
  await deps.store.lotStates.update(input.lotId, (cur) => {
    if (!cur) return cur;
    const dropped = withoutAdds(mediaOf(cur));
    files = dropped.files;
    return markFailed({ ...cur, media: dropped.media }, { ...input.error, message: LOT_GONE_MESSAGE });
  });
  for (const id of files) {
    await deps.media?.delete(id).catch((err: unknown) => {
      deps.logError?.('change queue: a staged file could not be deleted', err instanceof Error ? err : new Error(String(err)));
      return false;
    });
  }
  deps.notify();
}

/**
 * Send a lot's queued photos and videos, oldest first, stopping at the first that does not land.
 *
 * @param deps - the store, transport and staged files.
 * @param input.lotId - the lot (already created on the server).
 * @param input.signal - aborts the request in flight.
 * @returns how many requests were sent and why it stopped.
 * @throws {ReplayTransportError} when the server cannot be reached or the session expired; the lot stays as it was.
 * @sideEffect Sends ingest writes; removes each sent add from the lot's state and deletes its staged files.
 */
export async function sendMediaAdds(deps: LotMediaSenderDeps, input: { lotId: string; signal: AbortSignal }): Promise<LotMediaOutcome> {
  const { store, transport } = deps;
  let sent = 0;
  for (;;) {
    const state = await store.lotStates.get(input.lotId);
    const add = state ? mediaOf(state).adds[0] : undefined;
    if (!state || !add) return { ended: state ? 'ok' : 'gone', sent };
    // A closed queue, or one whose scope is no longer the device's, reads and sends nothing more (BUG-2064).
    if (!(await deps.maySend())) return { ended: 'retry', sent };
    const prepared = await prepare(deps, { lotId: input.lotId, state, add });
    if (!prepared.ok) {
      const after = await store.lotStates.update(input.lotId, (cur) =>
        cur && (prepared.retry ? markRetryLater(cur, prepared.error) : markFailed(cur, prepared.error)),
      );
      deps.notify();
      return { ended: prepared.retry && after?.status !== 'failed' ? 'retry' : 'failed', sent };
    }
    let answer: { status: number; body: unknown };
    try {
      answer = await transport.sendMedia({ ...prepared.input, lotId: input.lotId, signal: input.signal });
    } catch (err) {
      throw new ReplayTransportError('change queue: the connection failed', { cause: err });
    }
    sent += 1;
    const outcome = classifyIngestResponse(answer);
    switch (outcome.kind) {
      case 'synced':
      case 'dropped':
        await finishAdd(deps, { lotId: input.lotId, add, files: prepared.files, landed: outcome.kind === 'synced' });
        break;
      case 'lot-gone':
        await dropGoneLot(deps, { lotId: input.lotId, error: outcome.error });
        return { ended: 'failed', sent };
      case 'failed':
        await store.lotStates.update(input.lotId, (cur) => cur && markFailed(cur, outcome.error));
        deps.notify();
        return { ended: 'failed', sent };
      case 'retry-later': {
        const after = await store.lotStates.update(input.lotId, (cur) => cur && markRetryLater(cur, outcome.error));
        deps.notify();
        return { ended: after?.status === 'failed' ? 'failed' : 'retry', sent };
      }
      case 'transport':
        throw new ReplayTransportError(`change queue: ${outcome.reason}`);
    }
  }
}
